import React from 'react';
import PropTypes from 'prop-types';
import Label from '../Common/ListingLabels';
import ListingPrice from '../Common/ListingPrice';
import ListingStreetAddress from '../Common/ListingStreetAddress';
import resx from '../../utilities/resx';
import { RESX_LISTINGS, RESX_DASHBOARD, RESX_GLOBAL } from '../../Constants';
import Image from '../Common/Image';
import IsSoldLabel from '../Common/IsSoldLabel';

const ListingItem = props => {
    const {
        Address,
        Bathrooms,
        Bedrooms,
        City,
        Country,
        InteriorSize,
        InteriorSizeUnit,
        IsSold,
        State,
        Images,
        ListingId,
        ListingTransactionType,
        MLSGroupId,
        SeoUrl,
        PropertyType,
        ListingType,
        Neighborhood,
        HoursToExpiry,
        MarketStatus,
        ExpirationDate
    } = props.listing;

    const fullAddress = `${Address}, ${City}, ${State}, ${Country}`;
    const isPublicListing = MLSGroupId === '00000000-0000-0000-0000-000000000000' || MLSGroupId === null;
    const showNeighborhood = Neighborhood !== 'OTHER' && Neighborhood !== '';
    const renderNeighborhood = <span className="neighborhood">{Neighborhood}, </span>;
    const showRooms = Bathrooms > 0 || Bedrooms > 0 || InteriorSize > 0;
    const showListingTypeInfo = PropertyType || ListingType === 1;
    const loginReturnUrl = `/login?ReturnUrl=${encodeURIComponent(window.location.pathname)}`;
    const isApprovedUser = RESAAS.User2.IsProfessional && RESAAS.User2.IsPendingProfessional === false;
    const isPreMarket = MarketStatus === 1;
    const isRegularUser = RESAAS.User2.IsProfessional === false;
    const hidePremarketDetails = isPreMarket && !isApprovedUser && !IsSold;
    const showUpsell = hidePremarketDetails && isPublicListing && !isRegularUser;

    if (!props.listing.WithdrawnRemainingDays && MarketStatus === 1) {
        let remainingDays;
        const oneDayMilliseconds = 1000 * 60 * 60 * 24;
        const expiryDate = Date.parse(`${ExpirationDate}Z`);
        const currentTimestamp = Date.now();
        const difference = expiryDate - currentTimestamp;
        remainingDays = Math.max(0, difference / oneDayMilliseconds);

        props.listing.WithdrawnRemainingDays = remainingDays;
    }

    const getInteriorSizeUnitText = interiorSizeUnit => {
        const sizeUnitList = [
            resx(RESX_DASHBOARD, 'SQUARE_FEET_ABBR'),
            resx(RESX_DASHBOARD, 'SQUARE_METERS_ABBR'),
            resx(RESX_DASHBOARD, 'ACRES_UNIT_TEXT')
        ];
        return sizeUnitList[interiorSizeUnit];
    };


    return (
        <article
            className="re-listing-item re-container re-container-outer"
            data-listingid={ListingId}
            itemScope
            itemType="http://schema.org/Place"
        >
            {!hidePremarketDetails ? (
                <div className="re-listing-image-wrapper">
                    <a href={SeoUrl} className="re-listing-tile-images" title={resx(RESX_LISTINGS, 'LISTING_DETAILS')}>
                        <Image
                            src={Images[0].Url}
                            alt={fullAddress}
                            width={400}
                            height={300}
                            attributes={{ itemProp: 'photo' }}
                        />
                    </a>
                </div>
            ) : (
                <div className="re-listing-image-wrapper re-premarket-image-wrapper">
                    <a
                        href={loginReturnUrl}
                        className={`re-listing-tile-images ${isRegularUser ? 're-disabled-link' : ''}`}
                        title={resx(RESX_LISTINGS, 'UP_SELL_SEE_LISTING_TEXT')}
                        data-mixpanel-name="Intent"
                        data-mixpanel-type="premarket-upgrade"
                        data-mixpanel-context="image"
                    >
                        <Image
                            src={Images[0].Url}
                            alt={fullAddress}
                            width={400}
                            height={300}
                            attributes={{ itemProp: 'photo' }}
                        />
                        <div className="re-premarket-upsell-text">
                            <div
                                className={`re-premarket-upsell-coming-soon-border ${
                                    isRegularUser ? 're-regular-user' : ''
                                }`}
                            >
                                <span className="re-premarket-upsell-coming-soon-text">
                                    {resx(RESX_LISTINGS, 'PUBLIC_PRE_MARKET_LISTING_LABEL')}
                                </span>
                            </div>
                            {showUpsell? (
                                    <div className="re-premarket-upsell-create-account-wrapper">
                                        <span className="re-premarket-upsell-create-account">
                                            {resx(RESX_LISTINGS, 'UP_SELL_SEE_LISTING_TEXT')}
                                        </span>
                                    </div>
                                ): null}
                        </div>
                    </a>
                </div>
            )}
            <div className="re-listing-detail-content">
                <div className="re-listing-details-wrapper">
                    {showUpsell? (
                            <div className="re-premarket-upsell-price-wrapper">
                                <a
                                    href={loginReturnUrl}
                                    className="re-premarket-upsell-price-link listing-details-price"
                                    title={resx(RESX_LISTINGS, 'UP_SELL_SEE_PRICE_TEXT')}
                                    data-mixpanel-name="Intent"
                                    data-mixpanel-type="premarket-upgrade"
                                    data-mixpanel-context="price"
                                >
                                    <span>{resx(RESX_LISTINGS, 'UP_SELL_SEE_PRICE_TEXT')}</span>
                                </a>
                            </div>
                        ): null}
                    <a
                        href={showUpsell ? '#' : SeoUrl}
                        title={
                            showUpsell
                                ? resx(RESX_LISTINGS, 'UP_SELL_SEE_PRICE_TEXT')
                                : resx(RESX_LISTINGS, 'LISTING_DETAILS')
                        }
                        className={`re-listing-details-link ${showUpsell ? 're-listing-details-link-upsell': ''}`}
                    >
                        {!hidePremarketDetails ? (
                            <div className="re-listing-public-price" data-testid='listingprice'>
                                <ListingPrice listing={props.listing} currencyClassName="re-listing-price-currency" />
                            </div>
                        ) : null}
                        {!hidePremarketDetails && (
                            <div
                                itemProp="address"
                                itemScope
                                itemType="http://schema.org/PostalAddress"
                                data-testid='listingaddress'
                            >
                                <ListingStreetAddress listing={props.listing} />
                            </div>
                        )}
                        <div data-testid='addressLocality'>
                            {hidePremarketDetails && isPublicListing ? (
                                <span className="re-premarket-upsell-address">
                                    {showNeighborhood && renderNeighborhood}
                                </span>
                            ) : null}
                            {!hidePremarketDetails && (
                              <span>{showNeighborhood && renderNeighborhood}</span>
                            )}
                            {City}
                            {City && State ? `, ${State}` : State}
                            {(City || State) && Country ? `, ${Country}` : Country}
                        </div>
                        <div className="re-listing-details-rooms">
                            {Bedrooms > 0 ? (
                                <span className="re-dot-spacer">
                                    {Bedrooms}{' '}
                                    {resx(RESX_LISTINGS, 'BEDROOMS_SHORT_TEXT')}
                                </span>
                            ) : null}

                            {Bathrooms > 0 ? (
                                <span className="re-dot-spacer">
                                    {Bathrooms}{' '}
                                    {resx(RESX_LISTINGS, 'BATHROOMS_SHORT_TEXT')}
                                </span>
                            ) : null}

                            {InteriorSize > 0 ? (
                                <span className="re-dot-spacer" data-testid="interiorSize">
                                    {InteriorSize}&nbsp;{getInteriorSizeUnitText(InteriorSizeUnit)}
                                </span>
                            ) : null}
                        </div>
                        {showListingTypeInfo && (
                            <div className="re-listing-type">
                                {PropertyType ? <p>{PropertyType}</p> : null}
                                {ListingType === 1 ? (
                                    <p>{resx(RESX_LISTINGS, 'CREATE_LISTING_FOR_COMMERCIAL')}</p>
                                ) : null}
                            </div>
                        )}
                    </a>
                </div>
                <div className="re-listing-public-action-wrapper">
                    <div className="re-listing-actions-icons">
                        <div className="re-listing-share">
                            <a
                                href={SeoUrl}
                                className="js-share re-btn re-btn-plain re-btn-icononly re-icon re-icon-md re-icon-clockwise"
                                data-share-type={isPublicListing ? 'publiclisting' : 'privatelisting'}
                                data-share-page={true}
                                title={resx(RESX_GLOBAL, 'SHARE_TEXT')}
                            />
                        </div>
                    </div>
                </div>
                <div className="re-listing-details-label">
                    <Label isOwner={false} {...props} />
                </div>
            </div>
        </article>
    );
};

ListingItem.propTypes = {
    listing: PropTypes.object
};

export default ListingItem;
